import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Target, CheckSquare, RefreshCw } from "lucide-react";
import type { Goal, Habit, Task, NoteLink } from "@shared/schema";
import { isFutureGoal } from "@/lib/goal-visibility";

interface LinkEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noteId: string;
  existingLinks: NoteLink[];
  onSave: (links: { entityType: string; entityId: string }[]) => void;
}

export default function LinkEntityDialog({ 
  open, 
  onOpenChange, 
  noteId, 
  existingLinks,
  onSave 
}: LinkEntityDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLinks, setSelectedLinks] = useState<Set<string>>(
    new Set(existingLinks.map(link => `${link.entityType}:${link.entityId}`))
  );

  const { data: allGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  // Filter out completed/achieved goals - only show active goals for linking
  // Check both status and progress (100% progress = achieved)
  const goals = allGoals.filter(g => g.status !== "completed" && g.progress < 100 && !isFutureGoal(g));

  const { data: habits = [] } = useQuery<Habit[]>({
    queryKey: ["/api/habits"],
  });

  const { data: tasks = [] } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
  });

  const toggleLink = (entityType: string, entityId: string) => {
    const key = `${entityType}:${entityId}`;
    const newSelected = new Set(selectedLinks);
    
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    
    setSelectedLinks(newSelected);
  };

  const handleSave = () => {
    const links = Array.from(selectedLinks).map(link => {
      const [entityType, entityId] = link.split(":");
      return { entityType, entityId };
    });
    onSave(links);
  };

  const filterEntities = <T extends { title?: string; name?: string }>(
    entities: T[],
    search: string
  ): T[] => {
    if (!search) return entities;
    const searchLower = search.toLowerCase();
    return entities.filter(entity => {
      const text = (entity.title || entity.name || "").toLowerCase();
      return text.includes(searchLower);
    });
  };

  const filteredGoals = filterEntities(goals, searchTerm);
  const filteredHabits = filterEntities(habits, searchTerm);
  const filteredTasks = filterEntities(tasks, searchTerm);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none flex flex-col" data-testid="dialog-link-entity">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Link to Entity</DialogTitle>
        </DialogHeader>

        <div className="mb-4 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search goals, habits, or tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="input-search-entities"
            />
          </div>
        </div>

        <Tabs defaultValue="goals" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="goals" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Goals ({filteredGoals.length})
            </TabsTrigger>
            <TabsTrigger value="habits" className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Habits ({filteredHabits.length})
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4" />
              Tasks ({filteredTasks.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="goals" className="flex-1 mt-4 overflow-hidden min-h-0">
            <ScrollArea className="h-full pr-4">
              {filteredGoals.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm ? "No goals found" : "No goals available"}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredGoals.map((goal) => {
                    const key = `goal:${goal.id}`;
                    const isLinked = selectedLinks.has(key);
                    
                    return (
                      <div
                        key={goal.id}
                        className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer"
                        onClick={() => toggleLink("goal", goal.id)}
                        data-testid={`entity-goal-${goal.id}`}
                      >
                        <Checkbox
                          checked={isLinked}
                          onCheckedChange={() => toggleLink("goal", goal.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{goal.title}</div>
                          {goal.description && (
                            <div className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {goal.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="habits" className="flex-1 mt-4 overflow-hidden min-h-0">
            <ScrollArea className="h-full pr-4">
              {filteredHabits.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm ? "No habits found" : "No habits available"}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredHabits.map((habit) => {
                    const key = `habit:${habit.id}`;
                    const isLinked = selectedLinks.has(key);
                    
                    return (
                      <div
                        key={habit.id}
                        className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer"
                        onClick={() => toggleLink("habit", habit.id)}
                        data-testid={`entity-habit-${habit.id}`}
                      >
                        <Checkbox
                          checked={isLinked}
                          onCheckedChange={() => toggleLink("habit", habit.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{habit.name}</div>
                          {habit.description && (
                            <div className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {habit.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="tasks" className="flex-1 mt-4 overflow-hidden min-h-0">
            <ScrollArea className="h-full pr-4">
              {filteredTasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm ? "No tasks found" : "No tasks available"}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredTasks.map((task) => {
                    const key = `task:${task.id}`;
                    const isLinked = selectedLinks.has(key);
                    
                    return (
                      <div
                        key={task.id}
                        className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer"
                        onClick={() => toggleLink("task", task.id)}
                        data-testid={`entity-task-${task.id}`}
                      >
                        <Checkbox
                          checked={isLinked}
                          onCheckedChange={() => toggleLink("task", task.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{task.title}</div>
                          {task.description && (
                            <div className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {task.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4 flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} data-testid="button-save-links">
            Save Links
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
